import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

const getProgreessPercent = (comp, total) =>
  total > 0 ? Math.round((comp / total) * 100) : 0;

const getStatusBarGradient = (status) => {
  switch (status?.toLowerCase()) {
    case "complete":   return "bg-gradient-to-r from-green-400 to-green-600";
    case "inprogress": return "bg-gradient-to-r from-blue-400 to-blue-600";
    case "notstarted": return "bg-gradient-to-r from-gray-300 to-gray-500";
    default:           return "bg-gray-300";
  }
};

const getStatusBadgeColor = (status) => {
  switch (status?.toLowerCase()) {
    case "complete":   return "bg-green-100 text-green-700";
    case "inprogress": return "bg-blue-100 text-blue-700";
    case "notstarted": return "bg-gray-100 text-gray-600";
    default:           return "bg-gray-200 text-gray-600";
  }
};

const statusObj = {
  inprogress: "IN PROGRESS",
  notstarted: "NOT STARTED",
  complete:   "COMPLETED",
};

const statusTableTitle = {
  "All Statuses": "Total Objects in Scope",
  inprogress:     "Objects in Progress",
  complete:       "Objects Completed",
  notstarted:     "Objects Not Started",
};

const headerData = [
  "OBJECT NAME", "TOTAL COLUMNS", "IN SCOPE", "MAPPED", "PROGRESS", "STATUS",
];

const StatusTable = ({
  data,
  status,
  selectedObjects,
  filteredMappingFiles,
  notStartedNames = [],
  inProgressNames = [],
  completeNames = [],
}) => {
  const [selectedObjectsName, setSelectedObjectsName] = useState([]);
  const [allObjectsName, setAllObjectsName]           = useState([]);

  useEffect(() => {
    setSelectedObjectsName(
      selectedObjects.map((o) => o.description?.toLowerCase().trim())
    );
  }, [selectedObjects]);

  useEffect(() => {
    setAllObjectsName(
      filteredMappingFiles.map((o) => o.description?.toLowerCase().trim())
    );
  }, [filteredMappingFiles]);

  // For notstarted: the scoped-report data won't have these rows (0 scope),
  // so we render them directly from the names list the dashboard API returns.
  const renderNotStartedFromNames = () => {
    const filtered = notStartedNames.filter((name) =>
      selectedObjectsName.includes(name.toLowerCase().trim())
    );

    if (filtered.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="py-6 px-4 text-center text-sm text-gray-400">
            No objects match the current filter.
          </td>
        </tr>
      );
    }

    return filtered.map((name) => {
      // Try to find a matching row in scoped data (may or may not exist)
      const scopedRow = data?.find(
        (o) => o.objectDescription?.toLowerCase().trim() === name.toLowerCase().trim()
      );

      return (
        <tr key={name} className="border-b hover:bg-gray-50">
          <td className="py-3 px-4">{name}</td>
          <td className="py-3 px-4">{(scopedRow?.totalcolumns ?? 0).toLocaleString()}</td>
          <td className="py-3 px-4">{(scopedRow?.totalscopecount ?? 0).toLocaleString()}</td>
          <td className="py-3 px-4">{(scopedRow?.completedscopecount ?? 0).toLocaleString()}</td>
          <td className="py-3 px-4">
            <div className="flex items-center gap-2 min-w-[140px]">
              <div className="relative w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="h-2.5 rounded-full bg-gradient-to-r from-gray-300 to-gray-500"
                  style={{ width: "0%" }}
                />
              </div>
              <div className="text-sm text-gray-700 font-medium w-[40px] text-right">0%</div>
            </div>
          </td>
          <td className="py-3 px-4">
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 text-gray-600">
              NOT STARTED
            </span>
          </td>
        </tr>
      );
    });
  };

  const renderScopedRows = () =>
    data?.map((obj) => {
      if (
        status !== "All Statuses" &&
        obj.objectstatus?.toLowerCase() !== status.toLowerCase()
      ) return null;

      if (!selectedObjectsName.includes(obj.objectDescription?.toLowerCase().trim()))
        return null;

      if (!allObjectsName.includes(obj.objectDescription?.toLowerCase().trim()))
        return null;

      const percent = getProgreessPercent(obj.completedscopecount, obj.totalscopecount);

      return (
        <tr key={obj.objectid ?? obj.objectDescription} className="border-b hover:bg-gray-50">
          <td className="py-3 px-4">{obj.objectDescription}</td>
          <td className="py-3 px-4">{obj.totalcolumns.toLocaleString()}</td>
          <td className="py-3 px-4">{obj.totalscopecount.toLocaleString()}</td>
          <td className="py-3 px-4">{obj.completedscopecount.toLocaleString()}</td>
          <td className="py-3 px-4">
            <div className="flex items-center gap-2 min-w-[140px]">
              <div className="relative w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className={`h-2.5 rounded-full ${getStatusBarGradient(obj.objectstatus)}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className="text-sm text-gray-700 font-medium w-[40px] text-right">
                {percent}%
              </div>
            </div>
          </td>
          <td className="py-3 px-4">
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${getStatusBadgeColor(obj.objectstatus)}`}>
              {statusObj[obj.objectstatus]}
            </span>
          </td>
        </tr>
      );
    });

  return (
    <div className="objects-list">
      <h2 className="text-2xl font-semibold text-blue-900 mb-4 w-full">
        {statusTableTitle[status]}
      </h2>
      <div className="overflow-x-auto">
        <div className="max-h-[450px] overflow-y-auto">
          <table className="objects-table w-full border-collapse">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                {headerData.map((header) => (
                  <th key={header} className="text-left text-sm font-semibold text-gray-700 py-3 px-4">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {status === "notstarted"
                ? renderNotStartedFromNames()
                : renderScopedRows()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

StatusTable.propTypes = {
  data: PropTypes.array.isRequired,
  status: PropTypes.string.isRequired,
  selectedObjects: PropTypes.array.isRequired,
  filteredMappingFiles: PropTypes.array.isRequired,
  notStartedNames: PropTypes.arrayOf(PropTypes.string),
  inProgressNames: PropTypes.arrayOf(PropTypes.string),
  completeNames: PropTypes.arrayOf(PropTypes.string),
};

export default StatusTable;